import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Maximize, Minimize } from "lucide-react";
import MeetingTimelineView from "@/components/hfacs/meeting/MeetingTimelineView";
import MeetingFactorsView from "@/components/hfacs/meeting/MeetingFactorsView";
import MeetingDiagramView from "@/components/hfacs/meeting/MeetingDiagramView";
import MeetingPathsView from "@/components/hfacs/meeting/MeetingPathsView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIEWS = [
  { key: "timeline", label: "Chronologie des faits" },
  { key: "factors", label: "Facteurs HFACS retenus" },
  { key: "diagram", label: "Diagramme causal" },
  { key: "paths", label: "Chemins critiques" },
];

export default function MeetingMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [viewIdx, setViewIdx] = useState(0);
  const [data, setData] = useState({ analysis: null, facts: [], factors: [], links: [], paths: [] });
  const [isFullscreen, setIsFullscreen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, f, fa, l, p] = await Promise.all([
        axios.get(`${API}/hfacs/${id}`),
        axios.get(`${API}/hfacs/${id}/facts`),
        axios.get(`${API}/hfacs/${id}/factors`),
        axios.get(`${API}/hfacs/${id}/causal-links`),
        axios.get(`${API}/hfacs/${id}/critical-paths`),
      ]);
      setData({ analysis: a.data, facts: f.data, factors: fa.data, links: l.data, paths: p.data });
    } catch (e) {
      navigate(`/hfacs/${id}`);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const goPrev = useCallback(() => setViewIdx((v) => (v - 1 + VIEWS.length) % VIEWS.length), []);
  const goNext = useCallback(() => setViewIdx((v) => (v + 1) % VIEWS.length), []);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "Escape") navigate(`/hfacs/${id}`);
    };
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [goPrev, goNext, navigate, id]);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const exit = async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    navigate(`/hfacs/${id}`);
  };

  const activeView = VIEWS[viewIdx].key;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="meeting-mode-page">
      <header className="border-b border-border/60 px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <p className="font-mono text-xs uppercase text-muted-foreground">{id} · Mode Réunion</p>
          <h1 className="font-heading uppercase font-black text-xl">{VIEWS[viewIdx].label}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="meeting-prev-button" onClick={goPrev} className="p-2 rounded-sm border border-border hover:border-primary/50 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-1.5 px-2" data-testid="meeting-view-dots">
            {VIEWS.map((v, i) => (
              <button key={v.key} data-testid={`meeting-dot-${v.key}`} onClick={() => setViewIdx(i)} className={`h-2 w-2 rounded-full transition-colors ${i === viewIdx ? "bg-primary" : "bg-secondary"}`} />
            ))}
          </div>
          <button data-testid="meeting-next-button" onClick={goNext} className="p-2 rounded-sm border border-border hover:border-primary/50 transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
          <button data-testid="meeting-fullscreen-button" onClick={toggleFullscreen} className="p-2 rounded-sm border border-border hover:border-primary/50 transition-colors ml-2">
            {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </button>
          <button data-testid="meeting-exit-button" onClick={exit} className="p-2 rounded-sm border border-border hover:border-destructive/50 hover:text-destructive transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-8 py-10">
        {activeView === "timeline" && <MeetingTimelineView facts={data.facts} />}
        {activeView === "factors" && <MeetingFactorsView factors={data.factors} />}
        {activeView === "diagram" && <MeetingDiagramView analysis={data.analysis} factors={data.factors} links={data.links} />}
        {activeView === "paths" && <MeetingPathsView paths={data.paths} factors={data.factors} />}
      </main>
    </div>
  );
}
